import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Input, Select, Tag, Space, Typography,
  Modal, notification, Statistic, Row, Col, Tooltip, Divider, Form, Badge,
} from 'antd';
import axios from 'axios';
import {
  SearchOutlined, EyeOutlined, CheckCircleOutlined,
  ClockCircleOutlined, CloseCircleOutlined, ReloadOutlined,
  HeartOutlined, DollarOutlined, GiftOutlined,
} from '@ant-design/icons';
import { baseUrl } from '../../config';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const STATUS_TAG = {
  Pending:  <Tag icon={<ClockCircleOutlined />}  color="orange">Pending</Tag>,
  Approved: <Tag icon={<CheckCircleOutlined />}  color="green">Approved</Tag>,
  Rejected: <Tag icon={<CloseCircleOutlined />}  color="red">Rejected</Tag>,
};

const TYPE_COLOR = {
  Monetary:       '#16a34a',
  'Goods/Items':  '#2563eb',
  Service:        '#7c3aed',
  Infrastructure: '#d97706',
  Other:          '#6b7280',
};

const ORG_TYPES  = ['Gurukul/Vidyapeeth','School/College','Temple/Mandir/Tirth','Hospital','NGO/Sansthan','ABRSVS Branch','Government','Orphanage/Ashram','Other'];
const CONTRIB_TYPES = ['Monetary','Goods/Items','Service','Infrastructure','Other'];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAmt(row) {
  if (row.contribution_type === 'Monetary' && row.amount) {
    return `₹${Number(row.amount).toLocaleString('en-IN')}`;
  }
  return row.quantity_desc || '—';
}

export default function ContributionsList() {
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [stats,        setStats]        = useState({});
  const [viewItem,     setViewItem]     = useState(null);
  const [reviewItem,   setReviewItem]   = useState(null);
  const [rejectNote,   setRejectNote]   = useState('');
  const [reviewing,    setReviewing]    = useState(false);

  const PAGE_SIZE = 30;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/contributions/stats`, { headers: auth() });
      if (res.data.status) setStats(res.data.result || {});
    } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: PAGE_SIZE,
        ...(search       ? { search }            : {}),
        ...(statusFilter ? { status: statusFilter }     : {}),
        ...(typeFilter   ? { contribution_type: typeFilter } : {}),
      });
      const res = await axios.get(`${baseUrl}/admin/contributions?${params}`, { headers: auth() });
      if (res.data.status) {
        setData(res.data.result  || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      notification.error({ message: 'Failed to load contributions', placement: 'topRight' });
    } finally { setLoading(false); }
  }, [page, search, statusFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleReview = async (action) => {
    if (action === 'reject' && !rejectNote.trim()) {
      notification.warning({ message: 'कृपया rejection reason दर्ज करें', placement: 'topRight' });
      return;
    }
    setReviewing(true);
    try {
      const res = await axios.patch(
        `${baseUrl}/admin/contributions/${reviewItem.id}/review`,
        { action, admin_notes: rejectNote },
        { headers: auth() }
      );
      if (res.data.status) {
        notification.success({ message: action === 'approve' ? 'Approved ✅' : 'Rejected ❌', placement: 'topRight' });
        setReviewItem(null);
        setRejectNote('');
        fetchData();
        fetchStats();
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setReviewing(false); }
  };

  const columns = [
    {
      title: '#', dataIndex: 'id', width: 60,
      render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Submitted By',
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.submitter_name}</Text>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{r.submitter_phone}</div>
        </div>
      ),
    },
    {
      title: 'Organization',
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.org_name}</Text>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            {r.org_type} {r.org_city ? `· ${r.org_city}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Type & Amount',
      render: (_, r) => (
        <div>
          <Tag color={TYPE_COLOR[r.contribution_type]} style={{ fontSize: 11 }}>{r.contribution_type}</Tag>
          <div style={{ fontSize: 12, marginTop: 2, fontWeight: 600, color: '#111' }}>{fmtAmt(r)}</div>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'contribution_date',
      render: (v) => <Text style={{ fontSize: 12 }}>{fmtDate(v)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => STATUS_TAG[v] || <Tag>{v}</Tag>,
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setViewItem(r)} />
          </Tooltip>
          {r.status === 'Pending' && (
            <Tooltip title="Review">
              <Button size="small" type="primary" onClick={() => { setReviewItem(r); setRejectNote(''); }}
                style={{ background: '#7a0000', borderColor: '#7a0000' }}>
                Review
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '12px 0' }}>

      {/* Stats Strip */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { label: 'Total',    value: stats.total    || 0, color: '#7a0000', icon: <HeartOutlined /> },
          { label: 'Pending',  value: stats.pending  || 0, color: '#d97706', icon: <ClockCircleOutlined /> },
          { label: 'Approved', value: stats.approved || 0, color: '#16a34a', icon: <CheckCircleOutlined /> },
          { label: 'Rejected', value: stats.rejected || 0, color: '#dc2626', icon: <CloseCircleOutlined /> },
          { label: 'Total ₹ Approved', value: `₹${Number(stats.total_approved_amount || 0).toLocaleString('en-IN')}`, color: '#7c3aed', icon: <DollarOutlined /> },
        ].map(s => (
          <Col key={s.label} xs={12} sm={8} md={5} lg={4}>
            <Card size="small" bodyStyle={{ padding: '10px 14px' }}
              style={{ borderLeft: `4px solid ${s.color}`, borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: s.color, fontSize: 18 }}>{s.icon}</span>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{s.label}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HeartOutlined style={{ color: '#7a0000' }} />
            <Title level={5} style={{ margin: 0 }}>Samajik Yogdan — Social Contributions</Title>
            <Badge count={stats.pending || 0} style={{ background: '#d97706' }} />
          </div>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => { fetchData(); fetchStats(); }}>Refresh</Button>
        }
        bodyStyle={{ padding: '16px' }}
      >

        {/* Filters */}
        <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Search name, phone, organization…"
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={5}>
            <Select style={{ width: '100%' }} placeholder="Status" value={statusFilter || undefined}
              onChange={v => { setStatusFilter(v || ''); setPage(1); }} allowClear>
              <Option value="Pending">Pending</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Rejected">Rejected</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Select style={{ width: '100%' }} placeholder="Contribution Type" value={typeFilter || undefined}
              onChange={v => { setTypeFilter(v || ''); setPage(1); }} allowClear>
              {CONTRIB_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Col>
        </Row>

        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            current: page, pageSize: PAGE_SIZE, total,
            onChange: setPage, showTotal: t => `Total ${t}`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* ── View Detail Modal ─────────────────────────────────────────────── */}
      <Modal
        open={!!viewItem}
        title={<span><HeartOutlined style={{ marginRight: 8, color: '#7a0000' }} />Contribution Detail</span>}
        onCancel={() => setViewItem(null)}
        footer={[
          viewItem?.status === 'Pending' && (
            <Button key="review" type="primary" onClick={() => { setReviewItem(viewItem); setViewItem(null); setRejectNote(''); }}
              style={{ background: '#7a0000' }}>
              Review This Entry
            </Button>
          ),
          <Button key="close" onClick={() => setViewItem(null)}>Close</Button>,
        ].filter(Boolean)}
        width={560}
      >
        {viewItem && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Status */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{STATUS_TAG[viewItem.status]}</div>

            <Divider orientation="left" orientationMargin={0} style={{ margin: '6px 0', fontSize: 12, color: '#6b7280' }}>Submitter</Divider>
            <Row gutter={12}>
              <Col span={12}><DetailRow label="Name" value={viewItem.submitter_name} /></Col>
              <Col span={12}><DetailRow label="Phone" value={viewItem.submitter_phone} /></Col>
            </Row>

            <Divider orientation="left" orientationMargin={0} style={{ margin: '6px 0', fontSize: 12, color: '#6b7280' }}>Organization</Divider>
            <Row gutter={12}>
              <Col span={12}><DetailRow label="Name" value={viewItem.org_name} /></Col>
              <Col span={12}><DetailRow label="Type" value={viewItem.org_type} /></Col>
              <Col span={12}><DetailRow label="Phone" value={viewItem.org_phone || '—'} /></Col>
              <Col span={12}><DetailRow label="Location" value={[viewItem.org_city, viewItem.org_state].filter(Boolean).join(', ') || '—'} /></Col>
            </Row>

            <Divider orientation="left" orientationMargin={0} style={{ margin: '6px 0', fontSize: 12, color: '#6b7280' }}>Contribution</Divider>
            <Row gutter={12}>
              <Col span={12}><DetailRow label="Type" value={<Tag color={TYPE_COLOR[viewItem.contribution_type]}>{viewItem.contribution_type}</Tag>} /></Col>
              <Col span={12}><DetailRow label="Amount / Quantity" value={fmtAmt(viewItem)} /></Col>
              <Col span={12}><DetailRow label="Date" value={fmtDate(viewItem.contribution_date)} /></Col>
              <Col span={12}><DetailRow label="Public" value={viewItem.is_public ? 'Yes' : 'No'} /></Col>
            </Row>
            {viewItem.purpose && <DetailRow label="Purpose" value={viewItem.purpose} />}
            {viewItem.description && <DetailRow label="Description" value={viewItem.description} />}

            {viewItem.admin_notes && (
              <>
                <Divider style={{ margin: '6px 0' }} />
                <DetailRow label="Rejection Reason" value={<Text type="danger">{viewItem.admin_notes}</Text>} />
              </>
            )}
            <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>Submitted: {fmtDate(viewItem.created_at)}</div>
          </div>
        )}
      </Modal>

      {/* ── Review Modal ──────────────────────────────────────────────────── */}
      <Modal
        open={!!reviewItem}
        title={<span>Review Contribution — <b>{reviewItem?.submitter_name}</b></span>}
        onCancel={() => { setReviewItem(null); setRejectNote(''); }}
        footer={null}
        width={500}
      >
        {reviewItem && (
          <div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <Text strong>{reviewItem.org_name}</Text>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {reviewItem.contribution_type} · {fmtAmt(reviewItem)} · {fmtDate(reviewItem.contribution_date)}
              </div>
              {reviewItem.purpose && <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>Purpose: {reviewItem.purpose}</div>}
            </div>

            <div style={{ marginBottom: 14 }}>
              <Text style={{ fontSize: 13, color: '#374151' }}>
                If rejecting, please provide a reason (will be shown to the user):
              </Text>
              <TextArea
                rows={3}
                style={{ marginTop: 8 }}
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                placeholder="Rejection reason (required for reject)…"
                maxLength={500}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button onClick={() => { setReviewItem(null); setRejectNote(''); }}>Cancel</Button>
              <Button danger loading={reviewing} onClick={() => handleReview('reject')}
                icon={<CloseCircleOutlined />}>
                Reject
              </Button>
              <Button type="primary" loading={reviewing} onClick={() => handleReview('approve')}
                icon={<CheckCircleOutlined />}
                style={{ background: '#16a34a', borderColor: '#16a34a' }}>
                Approve & Notify User
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111', marginTop: 1 }}>{value ?? '—'}</div>
    </div>
  );
}
